import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import { countryWithFieldChange, emptyCountry } from '../utils/zoneCountries';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };

    default:
      return state;
  }
};

export default function ZoneEditScreen() {
  const navigate = useNavigate();
  const params = useParams(); // safes the parameters (dynamic segments), defined in the URL, into the params variable: /product/:id
  const { id: zoneId } = params; // deconstracts the id out of the params variable and its name is set to productId

  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const [names, setNames] = useState({ en: '', de: '' });
  const [price, setPrice] = useState(0);
  const [countries, setCountries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newCountry, setNewCountry] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(`/api/zones/${zoneId}`);

        setNames(data.names || { en: '', de: '' });
        setPrice(data.price || 0);
        setCountries(data.countries || []);

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };

    fetchData();
  }, [zoneId]);

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(`/api/zones/${zoneId}`, {
        names,
        price,
        countries,
      });
      dispatch({ type: 'UPDATE_SUCCESS' });
      toast.success('Zone updated successfully');
      navigate('/admin/zones');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  const handleCountryChange = (field, lang, value) => {
    if (editingIndex !== null) {
      setCountries((prevCountries) => {
        const updatedCountries = [...prevCountries];
        updatedCountries[editingIndex] = countryWithFieldChange(
          updatedCountries[editingIndex],
          field,
          lang,
          value
        );
        return updatedCountries;
      });
    } else if (newCountry) {
      setNewCountry((prev) => countryWithFieldChange(prev, field, lang, value));
    }
  };

  const startEditing = (index) => {
    setEditingIndex(index);
    setNewCountry(null);
  };

  const saveCountry = () => {
    if (newCountry) {
      setCountries([...countries, newCountry]);
      setNewCountry(null);
    }
    setEditingIndex(null);
  };

  const addNewCountry = () => {
    setNewCountry(emptyCountry());
    setEditingIndex(null);
  };

  const removeCountry = (index) => {
    setEditingIndex(null);
    setCountries(countries.filter((_, i) => i !== index));
  };

  return (
    <Container>
      <title>Edit Zone</title>

      <h1 className="screen-heading">Edit Shipping Zone</h1>

      {loading || loadingUpdate ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : (
        <Form onSubmit={submitHandler}>
          {/* Zone Name Fields */}
          <Form.Group className="mb-3" controlId="name-en">
            <Form.Label>English Name</Form.Label>
            <Form.Control
              className="mobile-device-compatible"
              value={names.en}
              onChange={(e) => setNames({ ...names, en: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="name-de">
            <Form.Label>German Name</Form.Label>
            <Form.Control
              className="mobile-device-compatible"
              value={names.de}
              onChange={(e) => setNames({ ...names, de: e.target.value })}
              required
            />
          </Form.Group>

          {/* Shipping Price */}
          <Form.Group className="mb-3" controlId="price">
            <Form.Label>Shipping Price</Form.Label>
            <Form.Control
              type="number"
              className="mobile-device-compatible"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="countries">
            <Form.Label className="d-block">Countries</Form.Label>
            {countries.map((country, index) => (
              <span
                key={index}
                className="mx-2"
                style={{
                  border: '1px solid black',
                  borderRadius: '5px',
                  color: '#fff',
                  backgroundColor: '#1a1a1a',
                  fontSize: '14px',
                  padding: '2px 2px 2px 2px',
                }}
              >
                <Button
                  className="no-css"
                  variant="none"
                  style={{ color: '#fff' }}
                  onClick={() => startEditing(index)}
                >
                  {country?.countryCode}
                </Button>
              </span>
            ))}
          </Form.Group>

          {/* Country Edit Form */}
          {(editingIndex !== null || newCountry) && (
            <div
              className="mb-3 p-3 border rounded"
              style={{ fontSize: '14px' }}
            >
              <Form.Group className="mb-2">
                <Form.Label>Country Code</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].countryCode
                      : newCountry.countryCode
                  }
                  onChange={(e) =>
                    handleCountryChange('countryCode', '', e.target.value)
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Primary Name (English)</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.en.primary
                      : newCountry.names.en.primary
                  }
                  onChange={(e) =>
                    handleCountryChange('primary', 'en', e.target.value)
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Special Territory (English)</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.en.specialTerritory
                      : newCountry.names.en.specialTerritory
                  }
                  onChange={(e) =>
                    handleCountryChange(
                      'specialTerritory',
                      'en',
                      e.target.value
                    )
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Supplementary Info (English)</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.en.supplementary
                      : newCountry.names.en.supplementary
                  }
                  onChange={(e) =>
                    handleCountryChange('supplementary', 'en', e.target.value)
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>
                  Secondary Names (English, comma-separated)
                </Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.en.secondary.join(', ')
                      : newCountry.names.en.secondary.join(', ')
                  }
                  onChange={(e) =>
                    handleCountryChange('secondary', 'en', e.target.value)
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Primary Name (German)</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.de.primary
                      : newCountry.names.de.primary
                  }
                  onChange={(e) =>
                    handleCountryChange('primary', 'de', e.target.value)
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Special Territory (German)</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.de.specialTerritory
                      : newCountry.names.de.specialTerritory
                  }
                  onChange={(e) =>
                    handleCountryChange(
                      'specialTerritory',
                      'de',
                      e.target.value
                    )
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Supplementary Info (German)</Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.de.supplementary
                      : newCountry.names.de.supplementary
                  }
                  onChange={(e) =>
                    handleCountryChange('supplementary', 'de', e.target.value)
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>
                  Secondary Names (German, comma-separated)
                </Form.Label>
                <Form.Control
                  value={
                    editingIndex !== null
                      ? countries[editingIndex].names.de.secondary.join(', ')
                      : newCountry.names.de.secondary.join(', ')
                  }
                  onChange={(e) =>
                    handleCountryChange('secondary', 'de', e.target.value)
                  }
                />
              </Form.Group>

              <Button variant="success" onClick={saveCountry} className="me-2">
                Save Country
              </Button>
              {editingIndex !== null && (
                <Button
                  variant="danger"
                  onClick={() => removeCountry(editingIndex)}
                >
                  Remove Country
                </Button>
              )}
            </div>
          )}

          <Button variant="secondary" onClick={addNewCountry} className="mb-3">
            Add Country
          </Button>

          <div className="d-flex justify-content-center align-items-center mt-4">
            <Button
              variant="secondary"
              className="mx-3"
              disabled={loadingUpdate}
              type="submit"
            >
              Save Zone
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
}
